import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, HelpCircle } from "lucide-react";
import { Wrap, H2, P, Prose, Impact, Disclaimer } from "@/components/Content";
import { CinematicSection } from "@/components/CinematicSection";
import { ASSETS } from "@/lib/api";
import { Seo, serviceSchema, breadcrumbSchema, faqSchema } from "@/components/Seo";
import { SEO_PAGES } from "@/data/seoPages";

const ORDER = [
  "pokemon-card-cleaning", "sports-card-cleaning", "baseball-card-cleaning", "card-grading-prep",
  "comic-cleaning", "comic-preservation", "mail-in-card-cleaning", "high-value-card-care", "dallas-card-cleaning",
];

const HERO = {
  "pokemon-card-cleaning": { img: "https://images.pexels.com/photos/37743085/pexels-photo-37743085.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", contain: false },
  "sports-card-cleaning": { img: "https://images.pexels.com/photos/7783409/pexels-photo-7783409.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", contain: false },
  "baseball-card-cleaning": { img: ASSETS.slabGriffey, contain: true },
  "card-grading-prep": { img: "https://images.unsplash.com/photo-1765747462642-bd654d86616a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", contain: false },
  "comic-cleaning": { img: ASSETS.comicAvengers4, contain: true },
  "comic-preservation": { img: "https://images.pexels.com/photos/34187516/pexels-photo-34187516.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", contain: false },
  "mail-in-card-cleaning": { img: "https://images.pexels.com/photos/6169055/pexels-photo-6169055.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", contain: false },
  "high-value-card-care": { img: "https://images.pexels.com/photos/6661034/pexels-photo-6661034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", contain: false },
  "dallas-card-cleaning": { img: "https://images.unsplash.com/photo-1653862496605-e994e87392ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", contain: false },
};

export default function ServiceLanding({ slug }) {
  const data = SEO_PAGES[slug];
  if (!data) return null;

  const hero = HERO[slug] || { img: ASSETS.slabJordan, contain: true };
  const reverse = ORDER.indexOf(slug) % 2 === 1;
  const h1lines = data.h1.split("\n");

  const jsonLd = [
    serviceSchema({
      name: data.serviceType,
      description: data.metaDescription,
      path: data.path,
      serviceType: data.serviceType,
    }),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Card Services", path: "/card-services" },
      { name: data.serviceType, path: data.path },
    ]),
    faqSchema(data.faqs),
  ];

  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title={data.seoTitle}
        description={data.metaDescription}
        path={data.path}
        jsonLd={jsonLd}
      />

      <CinematicSection
        image={hero.img}
        contain={hero.contain}
        reverse={reverse}
        alt={data.serviceType}
        label={data.eyebrow}
        headline={h1lines[0]}
        accent={h1lines[1]}
        sentence={data.sub}
        primaryTo="/submit"
        primaryLabel="SUBMIT A COLLECTIBLE"
        secondaryTo="/before-after"
        secondaryLabel="View Results"
      />

      <Wrap className="py-16 space-y-14">
        <Prose>
          {data.intro.map((t, i) => (
            <P key={i}>{t}</P>
          ))}
          {data.impact && <Impact tone="holo">{data.impact}</Impact>}
        </Prose>

        {data.sections.map((s, i) => (
          <div key={i}>
            <H2>{s.heading}</H2>
            <Prose className="mt-5">
              {s.paragraphs.map((t, j) => (
                <P key={j}>{t}</P>
              ))}
            </Prose>
          </div>
        ))}

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="eyebrow mb-4">What We Do</p>
            <ul className="space-y-2.5">
              {data.doList.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[color:var(--chrome-1)]">
                  <Check size={18} className="text-[color:var(--cyan)] shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-4">What We Won't Do</p>
            <ul className="space-y-2.5">
              {data.dontList.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[color:var(--chrome-1)]">
                  <X size={18} className="text-[color:var(--magenta)] shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <H2>FREQUENTLY ASKED</H2>
          <div className="mt-6 space-y-4">
            {data.faqs.map((f, i) => (
              <div
                key={i}
                data-testid={`faq-${slug}-${i}`}
                className="rounded-2xl border border-[color:var(--line)] glass p-6"
              >
                <p className="flex items-start gap-3 text-white font-semibold">
                  <HelpCircle size={18} className="text-[color:var(--cyan)] shrink-0 mt-0.5" />
                  {f.q}
                </p>
                <p className="text-[color:var(--chrome-2)] leading-relaxed mt-3 pl-7">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <Disclaimer>
          <p>No grade is guaranteed. Past results do not guarantee future results.</p>
          <p>
            CardScience HQ is not affiliated with, endorsed by, sponsored by or approved by PSA, CGC,
            Beckett, SGC or any other independent grading company unless explicitly stated otherwise.
            Independent grading companies determine all grades.
          </p>
        </Disclaimer>

        <div className="flex flex-wrap gap-3">
          <Link to="/submit" data-testid="landing-submit-cta" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">
            SUBMIT A COLLECTIBLE <ArrowRight size={16} />
          </Link>
          <Link to="/how-it-works" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">
            HOW IT WORKS
          </Link>
        </div>

        {data.related?.length > 0 && (
          <div className="pt-4 border-t border-[color:var(--line)]">
            <p className="eyebrow mb-4">Related</p>
            <div className="flex flex-wrap gap-3">
              {data.related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  data-testid={`related-${r.to.replace(/\//g, "")}`}
                  className="text-sm text-[color:var(--chrome-2)] hover:text-white border border-[color:var(--line)] rounded-full px-4 py-2 transition-colors hover:border-[color:var(--cyan)]"
                >
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </Wrap>
    </div>
  );
}
